import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router";
import styled from "styled-components";
import { config } from "../config";
import { Card, Muted, StatusPill, Surface } from "./ui";
import Workout from "./workouts/Workout";
import { GetWorkoutResponse, WorkoutDocument, WorkoutStepPayload } from "./workouts/types";

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const BackLink = styled(Link)`
  color: #2563eb;
  font-weight: 600;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  width: fit-content;

  &:hover {
    text-decoration: underline;
  }
`;

const ErrorText = styled.div`
  color: #dc2626;
  font-weight: 600;
`;

const Hero = styled(Surface)`
  margin-top: 1.5rem;
  display: grid;
  gap: 1rem;
`;

const HeroTitle = styled.h2`
  margin: 0;
  font-size: clamp(1.35rem, 2.8vw, 1.75rem);
  color: #0f172a;
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 0.8rem;
`;

const InfoCard = styled.div`
  border-radius: 16px;
  padding: 0.85rem 0.95rem;
  background: rgba(15, 23, 42, 0.04);
  border: 1px solid rgba(148, 163, 184, 0.26);
`;

const Label = styled.p`
  margin: 0;
  color: #64748b;
  font-size: 0.8rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const Value = styled.p`
  margin: 0.35rem 0 0;
  color: #0f172a;
  font-size: 1rem;
  font-weight: 600;
`;

const WorkoutSection = styled(Surface)`
  margin-top: 1rem;
`;

const WorkoutDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [workout, setWorkout] = useState<WorkoutDocument | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!id) return;

    fetch(`${config.REACT_APP_API_BASE_URL}/workouts/${id}`, {
      credentials: "include",
    })
      .then((res) => {
        if (!res.ok) throw new Error(`Status ${res.status}`);
        return res.json() as Promise<GetWorkoutResponse>;
      })
      .then((data) => {
        setWorkout(data.workout ?? null);
        setLoading(false);
      })
      .catch((err) => {
        console.error(err);
        setError("Failed to load workout.");
        setLoading(false);
      });
  }, [id]);

  const summary = useMemo(() => {
    const steps = workout?.payload?.steps ?? [];
    return {
      stepCount: countAllSteps(steps),
      repeats: countRepeats(steps),
      estimatedDuration: formatSeconds(sumDurationInSeconds(steps)),
      lastUpdated: formatApiDate(workout?.createdAt),
    };
  }, [workout]);

  if (loading) {
    return (
      <Card>
        <StatusPill>Loading</StatusPill>
        <Muted>Retrieving workout details...</Muted>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <StatusPill>Error</StatusPill>
        <ErrorText>{error}</ErrorText>
      </Card>
    );
  }

  if (!workout) {
    return (
      <Card>
        <StatusPill>Missing</StatusPill>
        <Muted>No workout found for this ID.</Muted>
      </Card>
    );
  }

  return (
    <Card>
      <Header>
        <StatusPill>Workout details</StatusPill>
        <Muted>A runner-friendly overview of your session, so you can quickly understand what to expect before you head out.</Muted>
        <BackLink to="/workouts">&larr; Back to workouts</BackLink>
      </Header>

      <Hero>
        <HeroTitle>{workout.payload?.workoutName || workout.name || "Untitled workout"}</HeroTitle>
        {workout.payload?.description && <Muted>{workout.payload.description}</Muted>}

        <InfoGrid>
          <InfoCard>
            <Label>Sport</Label>
            <Value>{workout.payload?.sport || "Run"}</Value>
          </InfoCard>
          <InfoCard>
            <Label>Total steps</Label>
            <Value>{summary.stepCount}</Value>
          </InfoCard>
          <InfoCard>
            <Label>Repeat blocks</Label>
            <Value>{summary.repeats}</Value>
          </InfoCard>
          <InfoCard>
            <Label>Estimated moving time</Label>
            <Value>{summary.estimatedDuration}</Value>
          </InfoCard>
          <InfoCard>
            <Label>Added to library</Label>
            <Value>{summary.lastUpdated}</Value>
          </InfoCard>
        </InfoGrid>
      </Hero>

      <WorkoutSection>
        <Workout workout={workout.payload} />
      </WorkoutSection>
    </Card>
  );
};

function countAllSteps(steps: WorkoutStepPayload[]): number {
  return steps.reduce((acc, step) => {
    const nested = step.steps ? countAllSteps(step.steps) : 0;
    return acc + 1 + nested;
  }, 0);
}

function countRepeats(steps: WorkoutStepPayload[]): number {
  return steps.reduce((acc, step) => {
    const nested = step.steps ? countRepeats(step.steps) : 0;
    const isRepeat = step.type === "WorkoutRepeatStep" ? 1 : 0;
    return acc + isRepeat + nested;
  }, 0);
}

function sumDurationInSeconds(steps: WorkoutStepPayload[]): number {
  return steps.reduce((acc, step) => {
    const nested = step.steps ? sumDurationInSeconds(step.steps) : 0;
    const type = step.durationType?.toUpperCase();
    const estimated = type === "TIME" || type === "FIXED_REST" ? Number(step.durationValue || 0) : 0;
    return acc + nested + estimated;
  }, 0);
}

function formatSeconds(totalSeconds: number): string {
  if (!Number.isFinite(totalSeconds) || totalSeconds <= 0) return "Not available";

  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);

  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }

  return `${minutes} min`;
}

function formatApiDate(value: { _seconds: number } | string | null | undefined): string {
  if (!value) return "Unknown";

  const date = typeof value === "string" ? new Date(value) : new Date(value._seconds * 1000);
  if (Number.isNaN(date.getTime())) return "Unknown";

  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

export default WorkoutDetailPage;
